import React, { useState, useRef, useEffect } from "react";
import { Modal, Box, List, ListItem, ListItemText, TextField, Popover, Tooltip, Zoom, Switch, FormControlLabel } from "@mui/material";
import { pdfjs } from "react-pdf";
import { Stage, Layer, Rect, Text, Label, Tag } from "react-konva";
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { SelectionMode, selectionModePlugin } from "@react-pdf-viewer/selection-mode";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { motion } from "framer-motion";
import { searchPlugin } from "@react-pdf-viewer/search";

// Import styles
import { styled } from "@mui/material/styles";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "@react-pdf-viewer/selection-mode/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";

// Set the worker source for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const MatchPdf = (props) => {
  let refs = {};
  const [currentLanguage, setCurrentLanguage] = useState("english");

  const handleLanguageToggle = () => {
    setCurrentLanguage((prevLanguage) => (prevLanguage === "english" ? "original" : "english"));
  };
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const searchPluginInstance = searchPlugin();
  const { CurrentPageLabel } = pageNavigationPluginInstance;

  const [numPages, setNumPages] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [inputPageNumber, setInputPageNumber] = useState(1);

  const [isHandToolActive, setIsHandToolActive] = useState(false);
  const pdfContainerRef = useRef(null);
  const [extractedBlocks, setExtractedBlocks] = useState([]);
  const [extractedText, setExtractedText] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredBox, setHoveredBox] = React.useState(null);

  const [loadedPages, setLoadedPages] = useState(new Map());
  const [totalHeight, setTotalHeight] = useState(0);

  const IOSSwitch = styled((props) => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(({ theme }) => ({
    width: 38,
    height: 20,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
          opacity: 1,
          border: 0,
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 16,
      height: 16,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
    },
  }));

  useEffect(() => {
    if (props?.openmatchpdf === false) {
      refs = {};
      // setExtractedText([]);
      // setExtractedBlocks([]);
      setNumPages();
      setPageNumber(1);
      setHoveredBox(null);
    }
  }, [props?.openmatchpdf]);

  useEffect(() => {
    if (props?.ocrData && props?.ocrData?.length > 0) {
      setLoading(true);

      // Filter data for the current page number
      const currentPageData = props.ocrData.filter((data) => data.page_number === pageNumber);

      // Extracting only text from the blocks along with id for the current page
      const data = currentPageData.flatMap((data) => {
        return data.blocks.map((block) => {
          return {
            id: block.id,
            text:
              currentLanguage === "english" ? (block.translated_text !== null && block.translated_text !== undefined && block.translated_text !== "" ? block.translated_text : block.text) : block.text,
          };
        });
      });

      setExtractedText(data);
      setLoading(false);

      // Assuming you still want to set extractedBlocks with all data, not filtered by page
      setExtractedBlocks(props.ocrData);
    }
  }, [props.ocrData, pageNumber, currentLanguage]);

  useEffect(() => {
    if (extractedText.length > 0) {
      refs = extractedText.reduce((acc, value) => {
        acc[value.id] = React.createRef();
        return acc;
      }, {});
    }
  }, [extractedText]);

  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });

  const scrollToItem = (blockId) => {
    const section = document.getElementById(blockId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Modal open={props.openmatchpdf} onClose={props.handleClosematchpdf} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box className="commonpopup modal-common popup-w85">
          <div className="card-item__content  d-flex align-items-center ">
            <div className="card-title">
              <h2>OCR PDF data</h2>
              <span>View, download or delete knowledge used by your brain</span>
            </div>
            <div className="switchlanguage" style={{ marginLeft: "auto", marginRight: "50px" }}>
              <Tooltip className="blue-tooltip" title="Change Language" TransitionComponent={Zoom} placement="top" arrow>
                <FormControlLabel
                  size="small"
                  control={<IOSSwitch size="small" sx={{ m: 1 }} fontSize="small" checked={currentLanguage === "english"} onChange={handleLanguageToggle} />}
                  label="English"
                />
              </Tooltip>
            </div>
            <span className="close" onClick={props.handleClosematchpdf} style={{ cursor: "default" }}>
              X
            </span>
          </div>
          <div className="modal-body">
            <div className="popup_scroller">
              <div className="match_pdf_wrapper">
                <div className="match_pdf_body">
                  <div className="match_content_flex">
                    <div className="match_content">
                      <div className="match_conten_body">
                        <div className="match_content_list">
                          <div className="readable_content">
                            <List sx={{ width: "100%", backgroundColor: "#fafafa" }}>
                              {!loading &&
                                extractedText &&
                                extractedText.length > 0 &&
                                extractedText.map((block) => {
                                  return (
                                    <ListItem
                                      alignItems="flex-start"
                                      ref={refs[block.id]}
                                      key={block.id}
                                      style={
                                        block.id === hoveredBox
                                          ? {
                                              backgroundColor: "rgba(131, 83, 226, 0.20)",
                                            }
                                          : {}
                                      }
                                    >
                                      <ListItemText className="section-scroll" id={block.id} primary={block.text} />
                                    </ListItem>
                                  );
                                })}
                            </List>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="match_conten_pdf">
                      <div className="right_chat_content_fix">
                        <div className="pdfView_wrapper">
                          <div className="pdfView_head">
                            <div className="pdf_title">
                              <h2>{props?.name ? props?.name : "NA"}</h2>
                            </div>
                          </div>
                          <div className="pdfView_body">
                            <CurrentPageLabel>{(props) => setPageNumber(props.currentPage + 1)}</CurrentPageLabel>

                            <div className="match_PDF_scroller">
                              <></>
                              <Worker workerUrl={`//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`}>
                                <Viewer
                                  fileUrl={props?.url}
                                  defaultScale={SpecialZoomLevel.PageFit}
                                  plugins={[
                                    defaultLayoutPluginInstance,
                                    pageNavigationPluginInstance,
                                    searchPluginInstance,
                                    // selectionModePluginInstance,
                                    {
                                      renderPageLayer: (props) => {
                                        let width, height;

                                        const pageData = extractedBlocks.find((text) => text.page_number === props.pageIndex + 1);

                                        if (!pageData) {
                                          return null;
                                        }

                                        width = props.width;
                                        height = props.height;
                                        const originalWidth = pageData.page_dimension.width;
                                        const originalHeight = pageData.page_dimension.height;
                                        let scaleX;
                                        let scaleY;
                                        if (extractedBlocks[props.pageIndex].page_orientation === "right" || extractedBlocks[props.pageIndex].page_orientation === "left") {
                                          scaleX = width / originalHeight;
                                          scaleY = height / originalWidth;
                                        } else {
                                          scaleX = width / originalWidth;
                                          scaleY = height / originalHeight;
                                        }

                                        return (
                                          <Stage
                                            width={originalWidth}
                                            height={originalHeight}
                                            style={{
                                              position: "absolute",
                                              top: 0,
                                              left: 0,
                                              //  pointerEvents: "none",
                                            }}
                                          >
                                            <Layer>
                                              {extractedBlocks
                                                .filter((text) => text.page_number === props.pageIndex + 1)
                                                .flatMap((text) =>
                                                  text.blocks.map((block) => {
                                                    if (block.vertices && block.vertices.length > 3) {
                                                      return (
                                                        <Rect
                                                          key={block.id}
                                                          className="konvajs-content"
                                                          x={block.vertices[0].x * scaleX}
                                                          y={block.vertices[0].y * scaleY}
                                                          width={(block.vertices[1].x - block.vertices[0].x) * scaleX}
                                                          height={(block.vertices[3].y - block.vertices[0].y) * scaleY}
                                                          stroke="red"
                                                          strokeWidth={1}
                                                          onClick={() => {
                                                            scrollToItem(block.id);
                                                          }} // highlight and scroll functionality
                                                          onMouseOver={(e) => {
                                                            setHoveredBox(block.id);
                                                            setTooltip({
                                                              show: true,
                                                              text: block.text,
                                                              page: props.pageIndex,
                                                              x: e.target.x(),
                                                              y: e.target.y() - 10, // Adjust as needed
                                                            });
                                                          }} // highlight and scroll functionality
                                                          onMouseOut={() => {
                                                            setHoveredBox(null);
                                                            setTooltip({ show: false, text: "", x: 0, y: 0 });
                                                          }}
                                                        />
                                                      );
                                                    }
                                                  })
                                                )}
                                            </Layer>
                                            {tooltip.show && tooltip.page === props.pageIndex && (
                                              <Layer>
                                                <Label x={tooltip.x} y={tooltip.y}>
                                                  <Tag
                                                    className="konvajs_content_tooltip"
                                                    position="absolute"
                                                    fill="fff"
                                                    fontSize="12px"
                                                    text-align="center"
                                                    zindex="999"
                                                    padding={5}
                                                    pointerDirection="down"
                                                    pointerWidth={10}
                                                    pointerHeight={10}
                                                    lineJoin="round"
                                                    shadowColor="black"
                                                    shadowBlur={10}
                                                    shadowOffsetX={10}
                                                    shadowOffsetY={10}
                                                    shadowOpacity={0.5}
                                                  />
                                                  <Text text={tooltip.text} padding={10} fill="white" style={{ fontSize: "12px" }} />
                                                </Label>
                                              </Layer>
                                            )}
                                          </Stage>
                                        );
                                      },
                                    },
                                  ]}
                                />
                              </Worker>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default MatchPdf;
